import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import { todayDateOnlyString } from '../utils/dateUtils';

interface CounsellorInfo {
  firstName: string;
  lastName: string;
  email: string;
  suspensionReason?: string;
  suspendedDate?: string;
}

export default function CredentialReactivation() {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [counsellor, setCounsellor] = useState<CounsellorInfo | null>(null);

  // Form state
  const [credentialType, setCredentialType] = useState<'DBS' | 'Insurance' | 'WorkPermit'>('DBS');
  const [dbsNumber, setDbsNumber] = useState('');
  const [dbsIssueDate, setDbsIssueDate] = useState(''); // Issue/print date from certificate
  const [insuranceProvider, setInsuranceProvider] = useState('');
  const [insuranceNumber, setInsuranceNumber] = useState('');
  const [insuranceExpiryDate, setInsuranceExpiryDate] = useState('');
  const [workPermitDetails, setWorkPermitDetails] = useState('');
  const [workPermitExpiryDate, setWorkPermitExpiryDate] = useState('');
  const [workPermitNoExpiry, setWorkPermitNoExpiry] = useState(false);
  const [certificate, setCertificate] = useState<File | null>(null);

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/credential-reactivation/verify-token/${token}`);
      setCounsellor(response.data.counsellor);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid or expired reactivation link');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF or image file (JPG, PNG)');
        return;
      }
      
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setCertificate(file);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!certificate) {
      setError('Please upload your certificate');
      return;
    }

    // Validate required fields based on credential type
    if (credentialType === 'DBS') {
      if (!dbsNumber || !dbsIssueDate) {
        setError('Please provide DBS number and issue date');
        return;
      }
    } else if (credentialType === 'Insurance') {
      if (!insuranceProvider || !insuranceNumber || !insuranceExpiryDate) {
        setError('Please provide insurance provider, number, and expiry date');
        return;
      }
    } else if (credentialType === 'WorkPermit') {
      if (!workPermitDetails || (!workPermitNoExpiry && !workPermitExpiryDate)) {
        setError('Please provide work permit details and an expiry date, or mark the permit as having no expiry date');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      const formData = new FormData();
      formData.append('certificate', certificate);
      formData.append('credentialType', credentialType);

      if (credentialType === 'DBS') {
        formData.append('dbsNumber', dbsNumber);
        formData.append('dbsIssueDate', dbsIssueDate); // Backend will calculate expiry (issue + 3 years)
      } else if (credentialType === 'Insurance') {
        formData.append('insuranceProvider', insuranceProvider);
        formData.append('insuranceNumber', insuranceNumber);
        formData.append('insuranceExpiryDate', insuranceExpiryDate);
      } else if (credentialType === 'WorkPermit') {
        formData.append('workPermitDetails', workPermitDetails);
        formData.append('workPermitNoExpiry', String(workPermitNoExpiry));
        formData.append('workPermitExpiryDate', workPermitNoExpiry ? '' : workPermitExpiryDate);
      }

      await api.post(`/credential-reactivation/submit-token/${token}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit credential. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error && !counsellor) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" color="error" gutterBottom>
              Invalid Link
            </Typography>
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              If you believe this is an error, please contact it.support@evolving-minds.org.uk
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h5" color="success.main">
                Submission Successful
              </Typography>
            </Box>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              Your credential has been submitted successfully and is now pending admin review.
            </Alert>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                What happens next?
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" color="text.secondary">
                  Our admin team will review your submission within 1-2 business days
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  You'll receive an email confirmation once reviewed
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  If approved, your account will be reactivated automatically
                </Typography>
                <Typography component="li" variant="body2" color="text.secondary">
                  If additional information is needed, we'll contact you via email
                </Typography>
              </Box>
            </Box>

            <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Questions? Contact us at <strong>it.support@evolving-minds.org.uk</strong>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 700, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Account Reactivation
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
            Submit your renewed credential to reactivate your account
          </Typography>

          {counsellor && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd', borderLeft: 4, borderColor: '#1976d2' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Account Information
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {counsellor.firstName} {counsellor.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {counsellor.email}
              </Typography>
              {counsellor.suspensionReason && (
                <Typography variant="body2">
                  <strong>Suspension Reason:</strong> {counsellor.suspensionReason}
                </Typography>
              )}
            </Paper>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Credential Type *</InputLabel>
              <Select
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value as any)}
                label="Credential Type *"
              >
                <MenuItem value="DBS">DBS Certificate</MenuItem>
                <MenuItem value="Insurance">Professional Indemnity Insurance</MenuItem>
                <MenuItem value="WorkPermit">Work Permit</MenuItem>
              </Select>
            </FormControl>

            {credentialType === 'DBS' && (
              <>
                <TextField
                  fullWidth
                  label="DBS Certificate Number *"
                  value={dbsNumber}
                  onChange={(e) => setDbsNumber(e.target.value)}
                  placeholder="Enter DBS certificate number"
                  required
                />
                <TextField
                  fullWidth
                  type="date"
                  label="DBS Issue Date *"
                  value={dbsIssueDate}
                  onChange={(e) => setDbsIssueDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: todayDateOnlyString() }}
                  helperText="Enter the issue/print date from your DBS certificate. Expiry will be calculated automatically (3 years)."
                  required
                />
              </>
            )}

            {credentialType === 'Insurance' && (
              <>
                <TextField
                  fullWidth
                  label="Insurance Provider *"
                  value={insuranceProvider}
                  onChange={(e) => setInsuranceProvider(e.target.value)}
                  placeholder="e.g., Towergate, Howden"
                  required
                />
                <TextField
                  fullWidth
                  label="Policy Number *"
                  value={insuranceNumber}
                  onChange={(e) => setInsuranceNumber(e.target.value)}
                  placeholder="Enter policy number"
                  required
                />
                <TextField
                  fullWidth
                  type="date"
                  label="Policy Expiry Date *"
                  value={insuranceExpiryDate}
                  onChange={(e) => setInsuranceExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </>
            )}

            {credentialType === 'WorkPermit' && (
              <>
                <TextField
                  fullWidth
                  label="Work Permit Details *"
                  value={workPermitDetails}
                  onChange={(e) => setWorkPermitDetails(e.target.value)}
                  placeholder="Enter work permit reference/details"
                  required
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={workPermitNoExpiry}
                      onChange={(e) => {
                        setWorkPermitNoExpiry(e.target.checked);
                        if (e.target.checked) setWorkPermitExpiryDate('');
                      }}
                    />
                  }
                  label="No expiry date (indefinite right to work)"
                />
                <TextField
                  fullWidth
                  type="date"
                  label={workPermitNoExpiry ? 'Work Permit Expiry Date' : 'Work Permit Expiry Date *'}
                  value={workPermitExpiryDate}
                  onChange={(e) => setWorkPermitExpiryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={workPermitNoExpiry}
                  required={!workPermitNoExpiry}
                />
              </>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Upload Certificate *
              </Typography>
              <Paper
                sx={{
                  border: 2,
                  borderStyle: 'dashed',
                  borderColor: certificate ? 'success.main' : 'grey.300',
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => document.getElementById('certificate-upload')?.click()}
              >
                <input
                  id="certificate-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <UploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
                {certificate ? (
                  <Typography color="success.main" fontWeight="medium">
                    {certificate.name}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" fontWeight="medium">
                      Click to upload certificate
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PDF or Image (Max 10MB)
                    </Typography>
                  </>
                )}
              </Paper>
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting || !certificate}
              fullWidth
              sx={{ mt: 2 }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting...
                </>
              ) : (
                'Submit for Review'
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Need help?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contact us at{' '}
              <a href="mailto:it.support@evolving-minds.org.uk" style={{ color: '#1976d2' }}>
                it.support@evolving-minds.org.uk
              </a>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
